import { collection, query, where, onSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '@config/firebase';

export interface CustomLabels {
  mentee?: string;
  mentor?: string;
  mentorship?: string;
}

export interface Tenant {
  id: string;
  title: string;
  adminEmail: string;
  description: string;
  disabled: boolean;
  userEmails: string[];
  userLimit: number;
  users: string[];
  createdAt?: any;
  role?: string; // Computed field for display
  customLabels?: CustomLabels;
}

export class TenantsService {
  private unsubscribeCallback: (() => void) | null = null;

  // Listen to tenants where adminEmail matches the current user's email
  subscribeToAdminTenants(userEmail: string, callback: (tenants: Tenant[]) => void) {
    if (this.unsubscribeCallback) {
      this.unsubscribeCallback();
    }

    const tenantsRef = collection(db, 'tenants');
    const q = query(tenantsRef, where('adminEmail', '==', userEmail));

    this.unsubscribeCallback = onSnapshot(
      q,
      (querySnapshot) => {
        const tenants: Tenant[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as DocumentData;
          tenants.push({
            id: data.id || doc.id,
            title: data.title || 'Unnamed Tenant',
            adminEmail: data.adminEmail,
            description: data.description || '',
            disabled: data.disabled || false,
            userEmails: data.userEmails || [],
            userLimit: data.userLimit || 0,
            users: data.users || [],
            createdAt: data.createdAt,
            role: 'Admin', // Since they're admin of this tenant
            customLabels: data.customLabels,
          });
        });
        callback(tenants);
      },
      (error) => {
        console.error('Error fetching tenants:', error);
        callback([]);
      }
    );
  }

  // Unsubscribe from the listener
  unsubscribe() {
    if (this.unsubscribeCallback) {
      this.unsubscribeCallback();
      this.unsubscribeCallback = null;
    }
  }
}

// Create a singleton instance
export const tenantsService = new TenantsService();